import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerIcon from '../../../components/custom_components/Icon'
import { PerInfoBoxNumberElement } from '../../../components/custom_components/InfoBox/InfoBoxNumberElement'
import { PerInfoBoxTagElement } from '../../../components/custom_components/InfoBox/InfoBoxTagElement'
import { InfoBoxTile } from '../../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerText from '../../../components/custom_components/Text'
import { Colors } from '../../../components/util/types'
import { EmployeeTagType, TalentsSeasonStatus } from '../../../types'
import { rankingUiProps } from '../../cultivatingTalents/ranking.uiprops'
import { useCultivatingTalentsStore } from '../../cultivatingTalents/CultivatingTalentsStore'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import PerButton from '../../../components/custom_components/Button'

export type PerformanceSectionProps = {
  isRankingEnabled: boolean
  maxTopTag?: number
  minLowTag?: number
  availableEmployeeTags: {
    tagName: string
    value: number
  }[]
  lowCounts: number
  topCounts: number
  performanceUntaggedCount: number
}

export type TagsSectionProps = {
  availableEmployeeTags: {
    tagName: string
    value: number
  }[]
  untaggedCount: number
}

export type SettingsSectionProps = {
  isHRSettings: boolean
  isAllCompaniesSettings: boolean
  isYourCompanyView: boolean
  onCompanySettingsClick: () => void
}

export const NumberSection: FC<{ totalOfEmployees: number }> = ({ totalOfEmployees }) => {
  const { t } = useTranslation()
  return (
    <InfoBoxTile heading={t('rankingView.infoBox.employeesHeading')} headingAlign="left" contentFlex="fixed">
      <PerInfoBoxNumberElement number={totalOfEmployees || '-'} />
    </InfoBoxTile>
  )
}

export const SettingsSection: FC<SettingsSectionProps> = ({
  isYourCompanyView,
  isHRSettings,
  isAllCompaniesSettings,
  onCompanySettingsClick,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)

  const [talentsSeasonSettings] = useCultivatingTalentsStore((state) => [state.talentsSeasonSettings], shallow)

  if (!talentsSeasonSettings) {
    return null
  }

  const renderColorStatus = (status: TalentsSeasonStatus): Colors => {
    switch (status) {
      case TalentsSeasonStatus.Closed:
        return 'secondary'
      case TalentsSeasonStatus.Confirmed:
        return 'success'
      case TalentsSeasonStatus.Opened:
      case TalentsSeasonStatus.Ready:
        return 'primary'
      default:
        return 'default'
    }
  }

  const renderIconStatus = (status: TalentsSeasonStatus) => {
    const icon =
      status === TalentsSeasonStatus.Closed
        ? 'close_stroke'
        : [TalentsSeasonStatus.Ready, TalentsSeasonStatus.Confirmed].includes(status)
        ? 'check_stroke'
        : status === TalentsSeasonStatus.Opened
        ? 'waiting_stroke'
        : 'close_stroke'

    return <PerIcon name={icon} color={renderColorStatus(status)} variant="decoration" size={16} />
  }

  const renderStatusFragment = () => {
    return (
      <PerText
        tooltip={
          [TalentsSeasonStatus.Confirmed].includes(talentsSeasonSettings.talentsSeasonStatus) &&
          talentsSeasonSettings.confirmationDate &&
          talentsSeasonSettings.confirmationEmployee
            ? t('companySettingsView.rankingTab.settings.confirmationDate', {
                confirmationDate: formatDateShort(talentsSeasonSettings.confirmationDate.toString()),
                employee: computeFullname(
                  user.nameDisplayPreference,
                  talentsSeasonSettings.confirmationEmployee.firstName,
                  talentsSeasonSettings.confirmationEmployee.lastName,
                ),
              })
            : ''
        }
        mt="s"
        color={renderColorStatus(talentsSeasonSettings.talentsSeasonStatus)}
        fontSize="s"
        className="whiteSpace--nowrap"
      >
        <PerFlexRow align="middle" justify="center">
          {renderIconStatus(talentsSeasonSettings.talentsSeasonStatus)}
          <PerSpacer ml="xxxs" />
          {t(`companySettingsView.rankingTab.settings.status${talentsSeasonSettings.talentsSeasonStatus}`)}
        </PerFlexRow>
      </PerText>
    )
  }

  return (
    <InfoBoxTile heading={t('rankingView.infoBox.settings.heading')} headingAlign="left" contentFlex="fixed">
      <PerFlexRow align="middle" justify="center" direction="column" className="per-infoBox--tagElement">
        <PerText appearance="label-small" align="center">
          {t('companySettingsView.rankingTab.settings.lowPercentage')}
        </PerText>
        <PerText mt="xs" fontSize="l" className="whiteSpace--nowrap">
          {[TalentsSeasonStatus.Ready].includes(talentsSeasonSettings.talentsSeasonStatus)
            ? '-'
            : talentsSeasonSettings.lowPercentage + '%'}
        </PerText>
      </PerFlexRow>
      <PerFlexRow align="middle" justify="center" direction="column" className="per-infoBox--tagElement">
        <PerText appearance="label-small" align="center">
          {t('companySettingsView.rankingTab.settings.topPercentage')}
        </PerText>
        <PerText mt="xs" fontSize="l" className="whiteSpace--nowrap">
          {[TalentsSeasonStatus.Ready].includes(talentsSeasonSettings.talentsSeasonStatus)
            ? '-'
            : talentsSeasonSettings.topPercentage + '%'}
        </PerText>
      </PerFlexRow>
      <PerFlexRow align="middle" justify="center" direction="column" className="per-infoBox--tagElement">
        <PerText appearance="label-small" align="center">
          {isAllCompaniesSettings || isHRSettings || isYourCompanyView
            ? t('companySettingsView.rankingTab.settings.rankingStatus')
            : t('companySettingsView.rankingTab.settings.status')}
        </PerText>
        {renderStatusFragment()}
      </PerFlexRow>
      {isHRSettings && (
        <PerFlexRow align="middle" justify="center" direction="column" className="per-infoBox--tagElement">
          <PerButton
            text={
              [TalentsSeasonStatus.Ready].includes(talentsSeasonSettings.talentsSeasonStatus)
                ? t('companySettingsView.rankingTab.settings.open')
                : t('companySettingsView.rankingTab.settings.edit')
            }
            color="primary"
            variant="pastel"
            disabled={talentsSeasonSettings.talentsSeasonStatus === TalentsSeasonStatus.Closed}
            onClick={onCompanySettingsClick}
          />
        </PerFlexRow>
      )}
    </InfoBoxTile>
  )
}

export const PerformanceSection: FC<PerformanceSectionProps> = ({
  isRankingEnabled = false,
  maxTopTag,
  minLowTag,
  availableEmployeeTags,
  lowCounts,
  topCounts,
  performanceUntaggedCount,
}) => {
  const { t } = useTranslation()
  const { tagColor, isPerformanceTag } = rankingUiProps()

  return (
    <InfoBoxTile heading={t('rankingView.infoBox.performance.heading')}>
      <PerInfoBoxTagElement
        name={t(`global.employeeTags.general.${EmployeeTagType.Low}`)}
        color={tagColor(EmployeeTagType.Low)}
        number={lowCounts}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        numberColor={isRankingEnabled ? (minLowTag! <= lowCounts ? undefined : 'error') : undefined}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        outOf={isRankingEnabled ? minLowTag! + ' ' + t('rankingView.infoBox.performance.minLowTagsPossibleLabel') : undefined}
      />
      <PerInfoBoxTagElement
        name={t(`global.employeeTags.general.${EmployeeTagType.Top}`)}
        color={tagColor(EmployeeTagType.Top)}
        number={topCounts}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        numberColor={isRankingEnabled ? (maxTopTag! >= topCounts ? undefined : 'error') : undefined}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        outOf={isRankingEnabled ? maxTopTag! + ' ' + t('rankingView.infoBox.performance.maxTopTagsPossibleLabel') : undefined}
      />
      {availableEmployeeTags
        .filter((t) => isPerformanceTag(t.tagName as EmployeeTagType))
        .filter(
          (t) => t.tagName !== EmployeeTagType.Top && t.tagName !== EmployeeTagType.Low && t.tagName !== EmployeeTagType.EntryLevelLow,
        )
        .map((employeeTag, index) => {
          return (
            <React.Fragment key={index}>
              <PerInfoBoxTagElement
                name={t(`global.employeeTags.general.${employeeTag.tagName}`)}
                color={tagColor(employeeTag.tagName)}
                number={employeeTag.value}
              />
            </React.Fragment>
          )
        })}

      <PerInfoBoxTagElement
        name={t('rankingView.infoBox.performance.untagged')}
        color={tagColor('untagged')}
        number={performanceUntaggedCount}
      />
    </InfoBoxTile>
  )
}

export const ImpactOfLossSection: FC<TagsSectionProps> = ({ availableEmployeeTags, untaggedCount }) => {
  const { t } = useTranslation()
  const { tagColor, isImpactOfLossTag } = rankingUiProps()
  return (
    <InfoBoxTile heading={t('rankingView.infoBox.impactOfLoss.heading')} headingAlign="left">
      {availableEmployeeTags
        .filter((t) => isImpactOfLossTag(t.tagName as EmployeeTagType))
        .map((employeeTag, index) => {
          return (
            <React.Fragment key={index}>
              {
                <PerInfoBoxTagElement
                  name={t(`global.employeeTags.impactOfLoss.${employeeTag.tagName}`)}
                  color={tagColor(employeeTag.tagName)}
                  number={employeeTag.value}
                />
              }
            </React.Fragment>
          )
        })}
      <PerInfoBoxTagElement name={t('rankingView.infoBox.impactOfLoss.untagged')} color={tagColor('untagged')} number={untaggedCount} />
    </InfoBoxTile>
  )
}

export const RiskOfLossSection: FC<TagsSectionProps> = ({ availableEmployeeTags, untaggedCount }) => {
  const { t } = useTranslation()
  const { tagColor, isRiskOfLossTag } = rankingUiProps()
  return (
    <InfoBoxTile heading={t('rankingView.infoBox.riskOfLoss.heading')} headingAlign="left">
      {availableEmployeeTags
        .filter((t) => isRiskOfLossTag(t.tagName as EmployeeTagType))
        .map((employeeTag, index) => {
          return (
            <React.Fragment key={index}>
              {
                <PerInfoBoxTagElement
                  name={t(`global.employeeTags.riskOfLoss.${employeeTag.tagName}`)}
                  color={tagColor(employeeTag.tagName)}
                  number={employeeTag.value}
                />
              }
            </React.Fragment>
          )
        })}
      <PerInfoBoxTagElement name={t('rankingView.infoBox.riskOfLoss.untagged')} color={tagColor('untagged')} number={untaggedCount} />
    </InfoBoxTile>
  )
}
